import * as React from "react";
import { Helmet } from "react-helmet";
import {
    Box,
    Typography,
    Container,
    Paper,
    Button,
    Grid,
    Stack,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import AddIcon from "@mui/icons-material/Add";
import AppAppBar from "../../../components/AppAppBar";
import Footer from "../../../components/Footer";

const THEME = {
    colors: {
        primary: "#ffca18",
        primaryDark: "#e6b800",
        secondary: "#1a1a1a",
        textSecondary: "#555",
        winnerBg: "#fffbea",
        winnerBorder: "#fdc916",
        loserBg: "#fff5f5",
        loserBorder: "#fcd5d5",
        neutralBg: "#f8f9fa",
        neutralBorder: "#e9ecef"
    }
};

const renderCell = (value) => {
    if (value === true) {
        return <CheckCircleIcon sx={{ color: "#1cae46" }} />;
    }
    if (value === false) {
        return <CancelIcon sx={{ color: "#d24545" }} />;
    }
    if (value === "partial") {
        return <RemoveCircleIcon sx={{ color: "#e0a800" }} />;
    }
    return (
        <Typography variant="body2" sx={{ fontWeight: 500, color: THEME.colors.secondary }}>
            {value}
        </Typography>
    );
};

const BackButton = () => {
    const navigate = useNavigate();
    return (
        <Button
            onClick={() => navigate("/compare")}
            startIcon={<ArrowBackIcon />}
            sx={{
                color: THEME.colors.textSecondary,
                fontWeight: 600,
                mb: 3,
                "&:hover": {
                    color: THEME.colors.primary,
                    transform: "translateX(-4px)",
                    bgcolor: "rgba(255,202,24,0.1)"
                }
            }}
        >
            All Comparisons
        </Button>
    );
};

export default function Compare({
    title,
    subtitle,
    heroImage,
    heroAlt,
    date,
    competitors = [],
    quickVerdict,
    intro,
    featureMatrix = [],
    sections = [],
    pricingMatrix = [],
    faqs = [],
    relatedCompares = [],
    meta = {}
}) {
    const location = useLocation();
    const baseUrl = "https://www.getmarter.com";
    const currentUrl = baseUrl + location.pathname;

    const publishDate = date ? new Date(date) : new Date();
    const isoDate = publishDate.toISOString();
    const formattedDate = publishDate.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric"
    });

    const productSchema = competitors.length > 0
        ? competitors.map((c) => ({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: c.name,
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web, Chrome Extension",
            offers: c.priceFrom
                ? {
                    "@type": "Offer",
                    price: c.priceFrom,
                    priceCurrency: "USD"
                }
                : undefined
        }))
        : [];

    const faqSchema = faqs.length > 0
        ? {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
                "@type": "Question",
                name: f.question,
                acceptedAnswer: {
                    "@type": "Answer",
                    text: f.answer
                }
            }))
        }
        : null;

    return (
        <>
            <Helmet>
                <title>{`${title} | Marter`}</title>
                <meta name="description" content={String(meta?.description || "")} />
                <meta name="keywords" content={String(meta?.keywords || "")} />
                <link rel="canonical" href={String(meta?.canonical || currentUrl)} />

                <meta property="og:title" content={title} />
                <meta property="og:description" content={String(meta?.description || "")} />
                <meta property="og:image" content={String(meta?.ogImage || heroImage)} />
                <meta property="og:url" content={String(meta?.canonical || currentUrl)} />
                <meta property="og:type" content="article" />
                <meta property="og:site_name" content="Marter - Walmart Seller Tool" />
                <meta property="article:published_time" content={isoDate} />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={title} />
                <meta name="twitter:description" content={String(meta?.description || "")} />
                <meta name="twitter:image" content={String(meta?.ogImage || heroImage)} />

                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Article",
                        headline: title,
                        description: meta?.description || "",
                        image: heroImage,
                        datePublished: isoDate,
                        author: {
                            "@type": "Person",
                            name: "Ido Friedman"
                        },
                        publisher: {
                            "@type": "Organization",
                            name: "Marter",
                            logo: {
                                "@type": "ImageObject",
                                url: `${baseUrl}/fulllogot.png`
                            }
                        },
                        mainEntityOfPage: {
                            "@type": "WebPage",
                            "@id": meta?.canonical || currentUrl
                        }
                    })}
                </script>

                {productSchema.length > 0 && (
                    <script type="application/ld+json">
                        {JSON.stringify(productSchema)}
                    </script>
                )}

                {faqSchema && (
                    <script type="application/ld+json">
                        {JSON.stringify(faqSchema)}
                    </script>
                )}
            </Helmet>

            <AppAppBar />

            <Box sx={{
                minHeight: "100vh",
                backgroundColor: "#f8f9fa",
                pt: { xs: 10, md: 12 },
                pb: { xs: 6, md: 10 }
            }}>
                <Container maxWidth="lg">
                    <BackButton />

                    <Paper elevation={0} sx={{
                        p: { xs: 3, md: 5 },
                        borderRadius: 3,
                        backgroundColor: "white",
                        boxShadow: "0 4px 24px rgba(0,0,0,0.06)"
                    }}>
                        <Chip
                            label="Comparison"
                            size="small"
                            sx={{
                                bgcolor: "rgba(255,202,24,0.15)",
                                color: THEME.colors.secondary,
                                fontWeight: 600,
                                mb: 2
                            }}
                        />

                        <Typography
                            variant="h1"
                            component="h1"
                            sx={{
                                fontSize: { xs: "2rem", md: "2.75rem" },
                                fontWeight: 800,
                                color: THEME.colors.secondary,
                                mb: 2,
                                lineHeight: 1.2
                            }}
                        >
                            {title}
                        </Typography>

                        {subtitle && (
                            <Typography
                                variant="h2"
                                sx={{
                                    fontSize: { xs: "1.1rem", md: "1.25rem" },
                                    color: THEME.colors.textSecondary,
                                    mb: 3,
                                    fontWeight: 400
                                }}
                            >
                                {subtitle}
                            </Typography>
                        )}

                        <Typography variant="body2" sx={{ color: THEME.colors.textSecondary, mb: 4 }}>
                            By Ido Friedman &nbsp;•&nbsp; Last updated {formattedDate}
                        </Typography>

                        {heroImage && (
                            <Box sx={{
                                mb: 5,
                                borderRadius: "16px",
                                overflow: "hidden",
                                boxShadow: "0 6px 32px rgba(0,0,0,0.08)",
                                width: "100%",
                                height: { xs: "220px", sm: "320px" }
                            }}>
                                <img
                                    src={heroImage}
                                    alt={heroAlt || title}
                                    onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        display: "block"
                                    }}
                                />
                            </Box>
                        )}

                        {quickVerdict && (
                            <Box sx={{
                                bgcolor: THEME.colors.winnerBg,
                                border: `1.5px solid ${THEME.colors.winnerBorder}`,
                                borderRadius: 2,
                                p: 3,
                                mb: 5
                            }}>
                                <Typography variant="subtitle1" sx={{
                                    fontWeight: 700,
                                    color: "#b8860b",
                                    mb: 1,
                                    textTransform: "uppercase",
                                    fontSize: "0.8rem",
                                    letterSpacing: 1
                                }}>
                                    Quick Verdict
                                </Typography>
                                <Typography variant="body1" sx={{
                                    fontSize: "1.05rem",
                                    lineHeight: 1.8,
                                    color: THEME.colors.secondary
                                }}>
                                    {quickVerdict}
                                </Typography>
                            </Box>
                        )}

                        {intro && (
                            <Box sx={{ mb: 5 }}>
                                <Typography variant="body1" sx={{
                                    fontSize: "1.1rem",
                                    lineHeight: 1.8,
                                    color: THEME.colors.textSecondary
                                }}>
                                    {intro}
                                </Typography>
                            </Box>
                        )}

                        {competitors.length > 0 && (
                            <Grid container spacing={3} sx={{ mb: 5 }}>
                                {competitors.map((c, i) => (
                                    <Grid item xs={12} md={12 / Math.min(competitors.length, 3)} key={i}>
                                        <Paper sx={{
                                            p: 3,
                                            borderRadius: 2,
                                            border: c.recommended
                                                ? `2px solid ${THEME.colors.primary}`
                                                : "1px solid #e9ecef",
                                            bgcolor: c.recommended ? THEME.colors.winnerBg : "white",
                                            height: "100%",
                                            position: "relative"
                                        }}>
                                            {c.recommended && (
                                                <Chip
                                                    label="Best for Walmart"
                                                    size="small"
                                                    sx={{
                                                        position: "absolute",
                                                        top: -12,
                                                        right: 16,
                                                        bgcolor: THEME.colors.primary,
                                                        color: THEME.colors.secondary,
                                                        fontWeight: 700
                                                    }}
                                                />
                                            )}
                                            <Typography variant="h5" sx={{
                                                fontWeight: 700,
                                                color: THEME.colors.secondary,
                                                mb: 1
                                            }}>
                                                {c.name}
                                            </Typography>
                                            <Typography variant="body2" sx={{
                                                color: THEME.colors.textSecondary,
                                                mb: 2,
                                                minHeight: "3em"
                                            }}>
                                                {c.tagline}
                                            </Typography>
                                            {c.priceFrom && (
                                                <Typography variant="h6" sx={{ color: THEME.colors.secondary, fontWeight: 700, mb: 2 }}>
                                                    {c.priceCurrency === "EUR" ? "€" : "$"}{c.priceFrom}/mo
                                                </Typography>
                                            )}
                                            <Typography variant="caption" sx={{
                                                display: "block",
                                                color: THEME.colors.textSecondary,
                                                mb: 1,
                                                fontWeight: 600
                                            }}>
                                                BEST FOR
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: THEME.colors.secondary, mb: 2 }}>
                                                {c.bestFor}
                                            </Typography>
                                            {c.cta && (
                                                <Button
                                                    variant={c.recommended ? "contained" : "outlined"}
                                                    href={c.cta.url}
                                                    target={c.cta.external ? "_blank" : undefined}
                                                    rel={c.cta.external ? "noopener noreferrer" : undefined}
                                                    fullWidth
                                                    sx={{
                                                        mt: 1,
                                                        bgcolor: c.recommended ? THEME.colors.primary : "transparent",
                                                        color: THEME.colors.secondary,
                                                        borderColor: THEME.colors.secondary,
                                                        fontWeight: 600,
                                                        "&:hover": {
                                                            bgcolor: c.recommended ? THEME.colors.primaryDark : "rgba(0,0,0,0.04)"
                                                        }
                                                    }}
                                                >
                                                    {c.cta.label}
                                                </Button>
                                            )}
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        )}

                        {featureMatrix.length > 0 && (
                            <Box sx={{ mb: 5 }}>
                                <Typography variant="h4" sx={{
                                    fontWeight: 700,
                                    color: THEME.colors.secondary,
                                    fontSize: { xs: "1.5rem", md: "1.75rem" },
                                    mb: 3
                                }}>
                                    Feature-by-Feature Comparison
                                </Typography>
                                <TableContainer component={Paper} elevation={0} sx={{
                                    border: "1px solid #e9ecef",
                                    borderRadius: 2
                                }}>
                                    <Table>
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: THEME.colors.neutralBg }}>
                                                <TableCell sx={{ fontWeight: 700, color: THEME.colors.secondary }}>
                                                    Feature
                                                </TableCell>
                                                {competitors.map((c, i) => (
                                                    <TableCell
                                                        key={i}
                                                        align="center"
                                                        sx={{
                                                            fontWeight: 700,
                                                            color: THEME.colors.secondary,
                                                            bgcolor: c.recommended ? "rgba(255,202,24,0.1)" : undefined
                                                        }}
                                                    >
                                                        {c.name}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {featureMatrix.map((row, i) => (
                                                <TableRow key={i}>
                                                    <TableCell sx={{ fontWeight: 600, color: THEME.colors.secondary }}>
                                                        {row.feature}
                                                    </TableCell>
                                                    {row.values.map((v, j) => (
                                                        <TableCell
                                                            key={j}
                                                            align="center"
                                                            sx={{
                                                                bgcolor: competitors[j]?.recommended ? "rgba(255,202,24,0.05)" : undefined
                                                            }}
                                                        >
                                                            {renderCell(v)}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <Stack direction="row" spacing={2} sx={{ mt: 2, flexWrap: "wrap" }}>
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <CheckCircleIcon sx={{ color: "#1cae46", fontSize: 18 }} />
                                        <Typography variant="caption">Yes</Typography>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <RemoveCircleIcon sx={{ color: "#e0a800", fontSize: 18 }} />
                                        <Typography variant="caption">Partial</Typography>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <CancelIcon sx={{ color: "#d24545", fontSize: 18 }} />
                                        <Typography variant="caption">No</Typography>
                                    </Stack>
                                </Stack>
                            </Box>
                        )}

                        {sections.length > 0 && (
                            <Box sx={{
                                "& section": {
                                    mb: 5,
                                    scrollMarginTop: "80px"
                                }
                            }}>
                                {sections.map((section, i) => (
                                    <section id={section.id} key={i}>
                                        <Typography variant="h4" sx={{
                                            fontWeight: 700,
                                            color: THEME.colors.secondary,
                                            fontSize: { xs: "1.5rem", md: "1.75rem" },
                                            mb: 2,
                                            mt: 4
                                        }}>
                                            {section.heading}
                                        </Typography>
                                        <Typography variant="body1" sx={{
                                            fontSize: "1.1rem",
                                            lineHeight: 1.8,
                                            color: THEME.colors.textSecondary
                                        }}>
                                            {section.body}
                                        </Typography>
                                        {i < sections.length - 1 && <Divider sx={{ mt: 4 }} />}
                                    </section>
                                ))}
                            </Box>
                        )}

                        {pricingMatrix.length > 0 && (
                            <Box sx={{ mb: 5, mt: 5 }}>
                                <Typography variant="h4" sx={{
                                    fontWeight: 700,
                                    color: THEME.colors.secondary,
                                    fontSize: { xs: "1.5rem", md: "1.75rem" },
                                    mb: 3
                                }}>
                                    Pricing Compared
                                </Typography>
                                <TableContainer component={Paper} elevation={0} sx={{
                                    border: "1px solid #e9ecef",
                                    borderRadius: 2
                                }}>
                                    <Table>
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: THEME.colors.neutralBg }}>
                                                <TableCell sx={{ fontWeight: 700 }}>Plan</TableCell>
                                                {competitors.map((c, i) => (
                                                    <TableCell
                                                        key={i}
                                                        align="center"
                                                        sx={{
                                                            fontWeight: 700,
                                                            bgcolor: c.recommended ? "rgba(255,202,24,0.1)" : undefined
                                                        }}
                                                    >
                                                        {c.name}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {pricingMatrix.map((row, i) => (
                                                <TableRow key={i}>
                                                    <TableCell sx={{ fontWeight: 600 }}>{row.tier}</TableCell>
                                                    {row.values.map((v, j) => (
                                                        <TableCell
                                                            key={j}
                                                            align="center"
                                                            sx={{
                                                                bgcolor: competitors[j]?.recommended ? "rgba(255,202,24,0.05)" : undefined
                                                            }}
                                                        >
                                                            {v}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        )}

                        {faqs.length > 0 && (
                            <Box sx={{ mb: 5, mt: 6 }}>
                                <Typography variant="h4" sx={{
                                    fontWeight: 700,
                                    color: THEME.colors.secondary,
                                    fontSize: { xs: "1.5rem", md: "1.75rem" },
                                    mb: 3
                                }}>
                                    Frequently Asked Questions
                                </Typography>
                                {faqs.map((faq, i) => (
                                    <Accordion key={i}>
                                        <AccordionSummary expandIcon={<AddIcon />}>
                                            <Typography sx={{ fontWeight: 600 }}>{faq.question}</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Typography sx={{ color: THEME.colors.textSecondary, lineHeight: 1.8 }}>
                                                {faq.answer}
                                            </Typography>
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </Box>
                        )}

                        <Box sx={{
                            mt: 6,
                            p: { xs: 3, md: 5 },
                            borderRadius: 3,
                            background: "linear-gradient(135deg, #FFCA18 0%, #FFB800 100%)",
                            textAlign: "center"
                        }}>
                            <Typography variant="h4" sx={{
                                fontWeight: 800,
                                color: THEME.colors.secondary,
                                fontSize: { xs: "1.5rem", md: "2rem" },
                                mb: 2
                            }}>
                                See Marter in Action
                            </Typography>
                            <Typography variant="body1" sx={{
                                color: THEME.colors.secondary,
                                fontSize: "1.1rem",
                                mb: 3
                            }}>
                                Built specifically for Walmart sellers. Free trial, no credit card required.
                            </Typography>
                            <Button
                                variant="contained"
                                endIcon={<ArrowForwardIcon />}
                                href="/"
                                sx={{
                                    bgcolor: THEME.colors.secondary,
                                    color: "white",
                                    py: 1.5,
                                    px: 5,
                                    fontWeight: 700,
                                    fontSize: "1.05rem",
                                    "&:hover": {
                                        bgcolor: "#333"
                                    }
                                }}
                            >
                                Start Free Trial
                            </Button>
                        </Box>

                        {relatedCompares.length > 0 && (
                            <Box sx={{ mt: 6 }}>
                                <Typography variant="h5" sx={{
                                    fontWeight: 700,
                                    color: THEME.colors.secondary,
                                    mb: 2
                                }}>
                                    More Comparisons
                                </Typography>
                                <Stack spacing={1}>
                                    {relatedCompares.map((rc, i) => (
                                        <Button
                                            key={i}
                                            href={rc.url}
                                            sx={{
                                                justifyContent: "flex-start",
                                                color: THEME.colors.secondary,
                                                textTransform: "none",
                                                fontSize: "1rem",
                                                fontWeight: 500,
                                                "&:hover": {
                                                    color: THEME.colors.primary,
                                                    bgcolor: "rgba(255,202,24,0.05)"
                                                }
                                            }}
                                        >
                                            → {rc.title}
                                        </Button>
                                    ))}
                                </Stack>
                            </Box>
                        )}
                    </Paper>
                </Container>
            </Box>

            <Footer />
        </>
    );
}
